import { Component, type ErrorInfo, type ReactNode } from 'react';
import { captureException } from '@/lib/observability';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/surface';

/**
 * The last line of defence above the router: a render error anywhere that no
 * route boundary caught. Reports to Sentry (a no-op without a DSN) and offers
 * a reload — the only thing guaranteed to recover a broken tree.
 */
export class AppErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    captureException(error, { componentStack: info.componentStack, boundary: 'app' });
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div role="alert" className="grid min-h-dvh place-items-center bg-canvas px-4 font-sans">
        <Card className="flex max-w-md flex-col items-center gap-4 p-8 text-center">
          <h1 className="text-[18px] font-semibold text-ink">Something went wrong</h1>
          <p className="text-[14px] text-ink-3">We've been told about it. Reloading usually fixes it — nothing you did was lost.</p>
          <Button variant="primary" onClick={() => window.location.reload()}>
            Reload
          </Button>
        </Card>
      </div>
    );
  }
}
